import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { Boxes, BookOpen, Briefcase, GraduationCap, Stethoscope } from "lucide-react";
import { HealthProductSuiteIcon } from "../HealthProductSuiteIcons";
import {
    AimmaProductSuiteGroupId,
    HealthLang,
} from "../healthTypes";
import {
    healthCardBody,
    healthCardTitle,
    healthEcosystemCardThemeForId,
    healthEcosystemPreviewCardCtaFor,
    healthEcosystemPreviewCardDisabledBorder,
    healthIntegrationPreviewCardRadius,
    healthIntegrationPreviewCardShell,
    healthEcosystemPreviewCardTintFor,
    healthIntegrationEcosystemCardBodyDisabled,
    healthIntegrationEcosystemCardStatusDisabled,
    healthLinkedCardFocusOutlineFor,
    healthLinkedCardTitleHoverFor,
    healthButtonArrowFa,
    healthLinkedCardHoverLift,
    healthLinkedCardInnerStable,
    healthNurseIntegrationCardLinkedBorderFor,
    healthProductSuiteIconGlyphFor,
    healthProductSuiteIconShellFor,
} from "../healthVisualStyles";
import { getIntegrationCardPreview } from "./HealthNurseIntegrationPreviews";

const ICON_STROKE = 1.75;

function comingSoonLabel(lang: HealthLang): string {
    if (lang === "Zh") return "即將推出";
    if (lang === "Jp") return "近日公開";
    return "Coming soon";
}

export interface HealthIntegrationPreviewCardProps {
    cardId: string;
    groupId: AimmaProductSuiteGroupId;
    title: string;
    body: string;
    ctaLabel: string;
    comingSoon: boolean;
    hLang: HealthLang;
    href?: string;
}

export function HealthIntegrationPreviewCard({
    cardId,
    groupId,
    title,
    body,
    ctaLabel,
    comingSoon,
    hLang,
    href,
}: HealthIntegrationPreviewCardProps) {
    const Preview = getIntegrationCardPreview(cardId);
    const isLinked = !comingSoon && Boolean(href);
    const cardTheme = healthEcosystemCardThemeForId(cardId);
    const borderClass = isLinked
        ? healthNurseIntegrationCardLinkedBorderFor(cardId)
        : healthEcosystemPreviewCardDisabledBorder;
    const tint = healthEcosystemPreviewCardTintFor(cardId);
    const ctaColor = healthEcosystemPreviewCardCtaFor(cardId);
    const PreviewComponent = Preview;
    const hasPreview = Boolean(PreviewComponent);
    // Mobile: preview sits flush on card bottom (no bottom padding); lg: absolute crop like desktop.
    const previewHostClass =
        cardId === "nurse"
            ? "pointer-events-none relative mt-5 w-[20.4rem] max-h-[11rem] shrink-0 overflow-hidden lg:absolute lg:left-[calc(52%+1rem)] lg:top-0 lg:mt-0 lg:max-h-none"
            : "pointer-events-none relative mt-5 w-full max-h-[11rem] max-w-[17rem] overflow-hidden lg:absolute lg:left-[calc(52%+1rem)] lg:top-0 lg:mt-0 lg:max-h-none lg:max-w-[17rem]";

    const innerPadClass = hasPreview
        ? "px-5 pt-5 pb-0 lg:p-6"
        : "p-5 lg:p-6";

    const content = (
        <article
            className={`group relative flex min-w-0 flex-col overflow-hidden border text-left ring-0 ${healthIntegrationPreviewCardRadius} ${borderClass} ${tint} ${
                hasPreview ? healthIntegrationPreviewCardShell : "h-full w-full"
            } ${
                isLinked
                    ? `cursor-pointer ${healthLinkedCardHoverLift}`
                    : "cursor-default transition-[box-shadow,border-color] duration-200 ease-out"
            }`}
        >
            <div
                className={`relative z-[1] flex min-h-full flex-1 flex-col text-left ${innerPadClass} ${isLinked ? healthLinkedCardInnerStable : ""}`}
            >
                <div className="flex w-full items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
                        {cardId === "hub" ? (
                            <span
                                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${healthProductSuiteIconShellFor("violet")} ${healthProductSuiteIconGlyphFor("violet")}`}
                                aria-hidden="true"
                            >
                                <Boxes
                                    className="h-[22px] w-[22px]"
                                    strokeWidth={ICON_STROKE}
                                />
                            </span>
                        ) : cardId === "education" ? (
                            <span
                                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${healthProductSuiteIconShellFor("violet")} ${healthProductSuiteIconGlyphFor("violet")}`}
                                aria-hidden="true"
                            >
                                <GraduationCap
                                    className="h-[22px] w-[22px]"
                                    strokeWidth={ICON_STROKE}
                                />
                            </span>
                        ) : cardId === "medical-sensei" ? (
                            <span
                                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${healthProductSuiteIconShellFor(cardTheme)} ${healthProductSuiteIconGlyphFor(cardTheme)}`}
                                aria-hidden="true"
                            >
                                <Stethoscope
                                    className="h-[22px] w-[22px]"
                                    strokeWidth={ICON_STROKE}
                                />
                            </span>
                        ) : cardId === "business-sensei" ? (
                            <span
                                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${healthProductSuiteIconShellFor(cardTheme)} ${healthProductSuiteIconGlyphFor(cardTheme)}`}
                                aria-hidden="true"
                            >
                                <Briefcase
                                    className="h-[22px] w-[22px]"
                                    strokeWidth={ICON_STROKE}
                                />
                            </span>
                        ) : cardId === "k12-sensei" ? (
                            <span
                                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${healthProductSuiteIconShellFor(cardTheme)} ${healthProductSuiteIconGlyphFor(cardTheme)}`}
                                aria-hidden="true"
                            >
                                <BookOpen
                                    className="h-[22px] w-[22px]"
                                    strokeWidth={ICON_STROKE}
                                />
                            </span>
                        ) : (
                            <HealthProductSuiteIcon
                                size="group"
                                groupId={groupId}
                                theme={cardTheme}
                                muted={comingSoon}
                            />
                        )}
                        <h3
                            className={`${healthCardTitle} min-w-0 text-grey-900 [text-align:left] ${
                                comingSoon
                                    ? "text-grey-600"
                                    : isLinked
                                      ? healthLinkedCardTitleHoverFor(cardTheme)
                                      : ""
                            }`}
                        >
                            {title}
                        </h3>
                    </div>
                    {comingSoon ? (
                        <span
                            className={`${healthIntegrationEcosystemCardStatusDisabled} shrink-0`}
                        >
                            {comingSoonLabel(hLang)}
                        </span>
                    ) : null}
                </div>

                <div className={`relative mt-3 ${hasPreview ? "max-lg:flex-none" : ""} lg:flex-1`}>
                    <div className="flex max-w-full flex-col items-start text-left [text-align:left] lg:max-w-[52%]">
                        <p
                            className={`${healthCardBody} w-full self-stretch leading-relaxed text-grey-600 [text-align:left] ${
                                comingSoon ? healthIntegrationEcosystemCardBodyDisabled : ""
                            }`}
                        >
                            {body}
                        </p>

                        {!comingSoon && ctaLabel ? (
                            <span
                                className={`mt-4 flex w-full items-center justify-start gap-1 self-start text-left text-sm font-semibold transition-colors duration-200 ease-out [text-align:left] ${ctaColor}`}
                            >
                                {ctaLabel}
                                <FontAwesomeIcon
                                    icon={faArrowRight}
                                    className={healthButtonArrowFa}
                                    aria-hidden="true"
                                />
                            </span>
                        ) : null}
                    </div>

                    {/* DECISION_LOG 2026-06: lg top-left anchor + overflow crop; mobile preview flush to card bottom */}
                    {PreviewComponent ? (
                        <div
                            className={`${previewHostClass} max-lg:-mx-5 max-lg:w-[calc(100%+2.5rem)] max-lg:max-w-none max-lg:pl-5`}
                            {...(isLinked ? { "aria-hidden": true } : {})}
                        >
                            <PreviewComponent lang={hLang} />
                        </div>
                    ) : null}
                </div>
            </div>
        </article>
    );

    if (isLinked && href) {
        return (
            <Link
                to={href}
                className={`block h-full no-underline ${healthLinkedCardFocusOutlineFor(cardTheme)}`}
            >
                {content}
            </Link>
        );
    }

    return content;
}

/** @deprecated Use `HealthIntegrationPreviewCard` */
export const HealthNurseIntegrationEcosystemCard = HealthIntegrationPreviewCard;

/** @deprecated Use `HealthIntegrationPreviewCardProps` */
export type HealthNurseIntegrationEcosystemCardProps = HealthIntegrationPreviewCardProps;
